import classNames from 'classnames'
import React from 'react'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import DeleteButton from '../../buttons/actions-button/DeleteButton'
import EditButton from '../../buttons/actions-button/EditButton'
import { canCurrentUserDeleteCargo } from '../../../utils/cargoDeletePermission'
import { formatDateToDDMMYYYY } from '../../../utils/date'
import { getAmount, getItemCurrency } from '../../../utils/getAmount'
import type { Cargo } from '../../../redux/reducers/cargos'
import styles from './CargoSideModal.module.css'

export const getCargoCounterpartyInfo = (cargo: Cargo) => {
  const fromName = cargo.from?.entity_name || cargo.from?.name || '—'
  const toName = cargo.to?.entity_name || cargo.to?.name || '—'
  if (cargo.payer === 1) {
    return `${fromName} ⊳`
  }
  return `⊳ ${toName}`
}

type UnpaidCargoCardProps = {
  item: Cargo
  isSelected: boolean
  isClient: boolean
  checkboxIdPrefix?: string
  onToggleSelect: (cargoId: number) => void
  onEdit: (item: Cargo) => void
  onDelete: (item: Cargo) => void
}

const UnpaidCargoCard: React.FC<UnpaidCargoCardProps> = ({
  item,
  isSelected,
  isClient,
  checkboxIdPrefix = 'shipment',
  onToggleSelect,
  onEdit,
  onDelete,
}) => {
  return (
    <div
      className={classNames(
        styles.bgWhite,
        styles.shipmentItem,
        styles.bxShadow,
        styles.border10,
        styles.small2,
        'pt-1 pb-1 mb-2'
      )}
    >
      <div className="row align-items-center ml-1 mr-1 mb-1">
        <div className="col pr-1 pl-1">
          <input
            type="checkbox"
            id={`${checkboxIdPrefix}_${item.id}`}
            checked={isSelected}
            onChange={() => onToggleSelect(item.id)}
          />
          <label htmlFor={`${checkboxIdPrefix}_${item.id}`}>
            <span className={classNames(styles.ellipsis2)}>
              <b>{item.description}</b>
            </span>
          </label>
        </div>
        <div className="col-auto pr-0 pl-1">
          {item.images &&
            Array.isArray(item.images) &&
            item.images.length > 0 && (
              <>
                <span
                  className={classNames(styles.small3, 'd-inline-block', 'mr-1')}
                >
                  {item.images.length}
                </span>
                <PhotoProvider
                  className={classNames(styles.photoProvider, 'd-inline-block')}
                >
                  {item.images.map((photo: any, index: number) => (
                    <PhotoView
                      key={photo.id}
                      src={photo?.image?.lg || photo?.image?.original}
                    >
                      {index === 0 ? (
                        <span
                          className={classNames(
                            styles.btn,
                            styles.btnSmall2,
                            styles.btnBlueOutline,
                            styles.btnIcon,
                            'd-inline-block'
                          )}
                        >
                          <i
                            className={classNames(styles.icon, styles.iconPicture)}
                          />
                        </span>
                      ) : (
                        <img style={{ display: 'none' }} alt="" />
                      )}
                    </PhotoView>
                  ))}
                </PhotoProvider>
              </>
            )}
        </div>
      </div>
      <div className="row ml-1 mr-1">
        <div className="col pl-1">
          <div className={classNames(styles.small3, 'mb-1')}>
            {!!item.has_tn && (
              <span
                className={classNames(
                  'd-inline-block pl-1 pr-1 mr-1',
                  styles.bgGreen,
                  styles.colorWhite,
                  styles.borderRadius5,
                  styles.small3
                )}
              >
                <b>Т/Н</b>
              </span>
            )}
            {getCargoCounterpartyInfo(item)}
          </div>
          <div className={classNames(styles.small3, 'row', 'pb-1', 'align-items-end')}>
            <div className="col-auto">
              <i
                className={classNames(styles.icon, styles.iconRoute, styles.icon10)}
              />{' '}
              <b>{item?.report?.number || 'б/н'}</b> &nbsp;{' '}
              {item?.report?.departure && (
                <span className={classNames(styles.colorGray, styles.small3)}>
                  от {formatDateToDDMMYYYY(item?.report?.departure)}
                </span>
              )}
            </div>
            <div className="col" style={{ textAlign: 'right' }}>
              <b className={classNames(styles.colorRed, styles.nowrap)}>
                {getAmount(item.amount ?? 0)} {getItemCurrency(item.currency)}
              </b>
              <br />
              {item.payment_method === 'cash' && (
                <i
                  className={classNames(
                    styles.icon,
                    styles.iconLike,
                    styles.filterGreen,
                    styles.icon15,
                    'mr-1'
                  )}
                />
              )}
              <span className={styles.colorGray}>
                {item.payment_method === 'cash'
                  ? 'Нал'
                  : item.payment_method === 'bank_transfer'
                    ? 'Безнал'
                    : null}
              </span>
            </div>
          </div>
        </div>
        <div className="col-auto pl-1 pr-0 d-flex align-items-end" style={{ textAlign: 'right' }}>
          <EditButton onClick={() => onEdit(item)} />
          {!isClient && canCurrentUserDeleteCargo(item) && (
            <DeleteButton onClick={() => onDelete(item)} />
          )}
        </div>
      </div>
    </div>
  )
}

export default UnpaidCargoCard
